// DNewsSelect - drop-in replacement for native <select>.
//
// Why: the native HTML select widget is drawn by the OS, so we can't
// style its option list (the popover the user sees when they click).
// This component renders a styled trigger button + a fully-styled
// popover so dropdowns match the rest of the dNews brand instead of
// looking like 1996.
//
// API mirrors the native select for easy replacement:
//   <DNewsSelect
//       value={selectedValue}
//       onChange={(newValue) => ...}
//       options={[{ value, label }, ...]}
//       placeholder="..."   // shown when no value selected
//       disabled
//       className="..."     // extra class on the trigger button
//   />
//
// Keyboard: ↑ / ↓ navigate, Enter selects, Esc closes. Type-ahead works
// for single-character matches too (press "j" to jump to the first
// option starting with J), which power users find faster than mousing.

import React, { useEffect, useRef, useState, useCallback } from "react";
import { FaChevronDown, FaCheck } from "react-icons/fa";
import "./dNews_Select.css";

export default function DNewsSelect({
    value,
    onChange,
    options = [],
    placeholder = "Select…",
    disabled = false,
    className = "",
}) {
    const [open, setOpen] = useState(false);
    // The keyboard-focused option (separate from the selected value).
    const [hoverIdx, setHoverIdx] = useState(-1);
    const rootRef = useRef(null);
    const listRef = useRef(null);
    const typeBufferRef = useRef("");
    const typeTimerRef = useRef(null);

    const selected = options.find(o => String(o.value) === String(value));

    // ── Close on outside click ───────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    // ── When opening, focus the currently-selected option so keyboard
    //    navigation has a sensible anchor.
    useEffect(() => {
        if (!open) { setHoverIdx(-1); return; }
        const i = options.findIndex(o => String(o.value) === String(value));
        setHoverIdx(i >= 0 ? i : 0);
    }, [open, value, options]);

    // ── Auto-scroll the hovered option into view (long lists) ───────────
    useEffect(() => {
        if (!open || hoverIdx < 0 || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${hoverIdx}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    }, [hoverIdx, open]);

    const pick = useCallback((opt) => {
        if (disabled || !opt) return;
        onChange?.(opt.value);
        setOpen(false);
    }, [disabled, onChange]);

    const onKey = (e) => {
        if (disabled) return;
        if (!open) {
            // When closed, ArrowDown / Space / Enter opens the list.
            if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }
        if (e.key === "Escape")    { e.preventDefault(); setOpen(false); return; }
        if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx(i => Math.min(options.length - 1, i + 1)); return; }
        if (e.key === "ArrowUp")   { e.preventDefault(); setHoverIdx(i => Math.max(0, i - 1)); return; }
        if (e.key === "Home")      { e.preventDefault(); setHoverIdx(0); return; }
        if (e.key === "End")       { e.preventDefault(); setHoverIdx(options.length - 1); return; }
        if (e.key === "Enter")     { e.preventDefault(); pick(options[hoverIdx]); return; }
        if (e.key === "Tab")       { setOpen(false); return; }
        // Type-ahead: collect printable chars into a 600ms-decaying buffer,
        // jump to the first option whose label starts with that buffer.
        if (e.key.length === 1) {
            typeBufferRef.current += e.key.toLowerCase();
            clearTimeout(typeTimerRef.current);
            typeTimerRef.current = setTimeout(() => { typeBufferRef.current = ""; }, 600);
            const match = options.findIndex(o => String(o.label).toLowerCase().startsWith(typeBufferRef.current));
            if (match >= 0) setHoverIdx(match);
        }
    };

    return (
        <div
            ref={rootRef}
            className={`dn-select ${open ? "open" : ""} ${disabled ? "disabled" : ""} ${className}`}
            onKeyDown={onKey}
        >
            <button
                type="button"
                className="dn-select-trigger"
                onClick={() => !disabled && setOpen(o => !o)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={`dn-select-value ${!selected ? "placeholder" : ""}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <FaChevronDown className={`dn-select-chevron ${open ? "open" : ""}`} />
            </button>
            {open && (
                <div className="dn-select-menu" role="listbox" ref={listRef}>
                    {options.length === 0 && (
                        <div className="dn-select-empty">No options</div>
                    )}
                    {options.map((opt, i) => {
                        const isSelected = String(opt.value) === String(value);
                        const isHover    = i === hoverIdx;
                        return (
                            <div
                                key={String(opt.value)}
                                data-idx={i}
                                className={`dn-select-option ${isSelected ? "selected" : ""} ${isHover ? "hover" : ""}`}
                                role="option"
                                aria-selected={isSelected}
                                onMouseEnter={() => setHoverIdx(i)}
                                onClick={() => pick(opt)}
                            >
                                <span className="dn-select-option-label">{opt.label}</span>
                                {isSelected && <FaCheck className="dn-select-option-check" />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
